// Local-only state for photos the user has staged but not yet uploaded.
// Each staged entry has a client-side `id` (not the server document UUID — that's
// assigned by the backend on POST). `previewUrl` is created via URL.createObjectURL
// and is revoked by the consuming component when the entry is dropped.

export type StagedPhoto =
  | { id: string; status: 'idle';      file: File; previewUrl: string }
  | { id: string; status: 'uploading'; file: File; previewUrl: string }
  | { id: string; status: 'error';     file: File; previewUrl: string; message: string }

export type StagedAction =
  | { type: 'add';            payload: { file: File } }
  | { type: 'remove';         payload: { id: string } }
  | { type: 'markUploading';  payload: { id: string } }
  | { type: 'markFailed';     payload: { id: string; message: string } }
  | { type: 'markUploaded';   payload: { id: string } }
  | { type: 'clear' }

const newId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `staged-${Date.now()}-${Math.random()}`)

export const stagedReducer = (state: StagedPhoto[], action: StagedAction): StagedPhoto[] => {
  switch (action.type) {
    case 'add':
      return [
        ...state,
        {
          file: action.payload.file,
          id: newId(),
          previewUrl: URL.createObjectURL(action.payload.file),
          status: 'idle'
        }
      ]
    case 'remove':
    case 'markUploaded':
      return state.filter((s) => s.id !== action.payload.id)
    case 'markUploading':
      return state.map((s) =>
        s.id === action.payload.id
          ? { file: s.file, id: s.id, previewUrl: s.previewUrl, status: 'uploading' as const }
          : s
      )
    case 'markFailed':
      return state.map((s) =>
        s.id === action.payload.id
          ? {
              file: s.file,
              id: s.id,
              message: action.payload.message,
              previewUrl: s.previewUrl,
              status: 'error' as const
            }
          : s
      )
    case 'clear':
      return []
    default:
      return state
  }
}
